import React, { useState, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface TimelineEvent {
  time: string
  label: string
  oldRate: number | null
  newRate: number
  type: 'neutral' | 'increase' | 'decrease' | 'override'
  changePct?: string
}

const EVENTS: TimelineEvent[] = [
  {
    time: 'Mon 08:00',
    label: 'Initial computation',
    oldRate: null,
    newRate: 100,
    type: 'neutral',
  },
  {
    time: 'Mon 14:00',
    label: "Season 'Summer' applied",
    oldRate: 100,
    newRate: 120,
    type: 'increase',
    changePct: '+20%',
  },
  {
    time: 'Tue 08:00',
    label: 'Recomputation: demand up',
    oldRate: 120,
    newRate: 128,
    type: 'increase',
    changePct: '+6.7%',
  },
  {
    time: 'Tue 16:00',
    label: 'Manual override set',
    oldRate: 128,
    newRate: 115,
    type: 'override',
  },
  {
    time: 'Wed 08:00',
    label: 'Override removed',
    oldRate: 115,
    newRate: 130,
    type: 'increase',
    changePct: 'recomputed',
  },
  {
    time: 'Thu 08:00',
    label: 'Recomputation: demand down',
    oldRate: 130,
    newRate: 122,
    type: 'decrease',
    changePct: '-6.2%',
  },
]

const DOT_COLORS: Record<TimelineEvent['type'], { bg: string; ring: string; text: string }> = {
  neutral: { bg: 'bg-slate-400', ring: 'ring-slate-100', text: 'text-slate-600' },
  increase: { bg: 'bg-emerald-500', ring: 'ring-emerald-50', text: 'text-emerald-600' },
  decrease: { bg: 'bg-rose-500', ring: 'ring-rose-50', text: 'text-rose-600' },
  override: { bg: 'bg-amber-500', ring: 'ring-amber-50', text: 'text-amber-600' },
}

const TYPE_LABELS: Record<TimelineEvent['type'], string> = {
  neutral: 'Initial',
  increase: 'Increase',
  decrease: 'Decrease',
  override: 'Override',
}

export default function RateTimelineWidget() {
  const [visibleCount, setVisibleCount] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Auto-play on mount
    let current = 0
    const reveal = () => {
      current += 1
      setVisibleCount(current)
      if (current < EVENTS.length) {
        timerRef.current = setTimeout(reveal, 900)
      }
    }
    timerRef.current = setTimeout(reveal, 600)

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return (
    <div className="space-y-1">
      {/* Timeline */}
      <div className="relative">
        {EVENTS.map((event, idx) => {
          const isVisible = idx < visibleCount
          const isLast = idx === EVENTS.length - 1
          const isFinal = isLast && visibleCount >= EVENTS.length
          const colors = DOT_COLORS[event.type]

          return (
            <div
              key={idx}
              className={cn(
                'relative flex items-start gap-4 transition-all duration-500',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
                idx < EVENTS.length - 1 ? 'pb-5' : 'pb-0'
              )}
            >
              {/* Vertical line + dot */}
              <div className="relative flex flex-col items-center">
                {/* Dot */}
                <div
                  className={cn(
                    'relative z-10 h-3.5 w-3.5 rounded-full ring-4 transition-all duration-300 shrink-0',
                    colors.bg,
                    colors.ring,
                    isFinal && 'h-4.5 w-4.5 ring-[5px]'
                  )}
                />
                {/* Connecting line */}
                {!isLast && (
                  <div
                    className={cn(
                      'w-px flex-1 min-h-8 transition-all duration-500',
                      isVisible ? 'bg-slate-200' : 'bg-transparent'
                    )}
                  />
                )}
              </div>

              {/* Content */}
              <div className={cn('flex-1 -mt-0.5 min-w-0', isFinal && 'pb-0')}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {/* Time + type tag */}
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] tabular-nums font-medium text-muted-foreground">
                        {event.time}
                      </span>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          event.type === 'increase' && 'bg-emerald-50 text-emerald-700',
                          event.type === 'decrease' && 'bg-rose-50 text-rose-700',
                          event.type === 'override' && 'bg-amber-50 text-amber-700',
                          event.type === 'neutral' && 'bg-slate-100 text-slate-600'
                        )}
                      >
                        {TYPE_LABELS[event.type]}
                      </span>
                    </div>

                    {/* Label */}
                    <p className="text-sm font-medium text-foreground mt-0.5">
                      {event.label}
                    </p>
                  </div>

                  {/* Rate change */}
                  <div className="text-right shrink-0">
                    {event.oldRate !== null ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm tabular-nums text-muted-foreground line-through">
                          £{event.oldRate}
                        </span>
                        <span className="text-sm text-muted-foreground">→</span>
                        <span
                          className={cn(
                            'text-sm tabular-nums font-bold',
                            isFinal ? 'text-lg' : '',
                            colors.text
                          )}
                        >
                          £{event.newRate}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm tabular-nums font-bold text-foreground">
                        £{event.newRate}
                      </span>
                    )}
                    {event.changePct && (
                      <span className={cn('text-[11px] tabular-nums font-medium', colors.text)}>
                        {event.changePct}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Final rate summary — only show when all events are visible */}
      <div
        className={cn(
          'mt-4 flex items-center justify-between rounded-lg border px-4 py-3 transition-all duration-500',
          visibleCount >= EVENTS.length
            ? 'opacity-100 translate-y-0 bg-primary/5 border-primary/20'
            : 'opacity-0 translate-y-2 bg-transparent border-transparent pointer-events-none'
        )}
      >
        <div>
          <p className="text-xs text-muted-foreground">Current rate after 4 recomputations</p>
          <p className="text-[11px] text-muted-foreground">
            Started at £{EVENTS[0].newRate}, peak £{Math.max(...EVENTS.map((e) => e.newRate))}
          </p>
        </div>
        <span className="text-2xl font-bold tabular-nums text-foreground tracking-tight">
          £{EVENTS[EVENTS.length - 1].newRate}
        </span>
      </div>
    </div>
  )
}
